/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import type { DescriptionPage_WeeklyMaterialItemType as DescriptionPageWeeklyMaterialItemType } from '__generated__/graphql-types';
import { DescriptionPage_WeeklyMaterialItemType as WeeklyMaterialItemType } from '__generated__/graphql-types';

import { ShowMoreContainer, Typography2 } from '@coursera/cds-core';
import {
  AssignmentCircledIcon,
  DiscussionPromptCircledIcon,
  LTICircledIcon,
  PluginIcon,
  ProgrammingAssignmentCircledIcon,
  QuizCircledIcon,
  ReadingCircledIcon,
  ReviewCircledIcon,
  VideoCircledIcon,
  WebCircledIcon,
} from '@coursera/cds-icons';

import useIsMobile from 'bundles/common/hooks/useIsMobile';
import ModuleMaterialGroup from 'bundles/unified-description-page-common/components/modules/syllabus/ModuleMaterialGroup';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';
import type { WeeklyMaterialItemFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCourseEntitiesFragments';

import _t from 'i18n!nls/xdp';

export type ModuleItemContentElement = React.ReactElement<Props, typeof ModuleItemContent>;

type Props = {
  description?: string | null;
  lectures?: WeeklyMaterialItemFragment[] | null;
  supplements?: WeeklyMaterialItemFragment[] | null;
  assessments?: Record<DescriptionPageWeeklyMaterialItemType, WeeklyMaterialItemFragment[]> | null;
  showLecturePreview?: boolean;
  courseSlug: string;
  authoringPartnerID?: string;
};

const AssessmentTypeOrder = [
  WeeklyMaterialItemType.Quiz,
  WeeklyMaterialItemType.Assignment,
  WeeklyMaterialItemType.ProgrammingAssignment,
  WeeklyMaterialItemType.PeerReview,
  WeeklyMaterialItemType.AppItem,
  WeeklyMaterialItemType.DiscussionPrompt,
  WeeklyMaterialItemType.UngradedLab,
  WeeklyMaterialItemType.Plugin,
  WeeklyMaterialItemType.TeammateReview,
  WeeklyMaterialItemType.WiseFlow,
];

const styles = {
  groupSummary: css`
    display: flex;
    align-items: center;
    flex-direction: row;
    flex-wrap: wrap;
    gap: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-100);
  `,
  groupLabel: css`
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-100);
  `,
  materials: css`
    margin-top: calc(var(--cds-spacing-200) * -1);
  `,
  showMoreContainer: css`
    padding-top: var(--cds-spacing-100);
  `,
};

const ModuleItemContent = ({
  description,
  lectures,
  supplements,
  assessments,
  showLecturePreview,
  courseSlug,
  authoringPartnerID,
}: Props) => {
  const isMobile = useIsMobile();

  const groups = useMemo(() => {
    const materialGroups: Array<{
      label: React.ReactNode;
      items: WeeklyMaterialItemFragment[];
      showPreviewLink?: boolean;
    }> = [];

    if (lectures && lectures.length > 0) {
      materialGroups.push({
        label: (
          <span css={styles.groupLabel}>
            <VideoCircledIcon size="large" />
            <FormattedMessage
              message={_t('{numberOfItems} {numberOfItems, plural, =1 {video} other {videos}}')}
              numberOfItems={lectures.length}
            />
          </span>
        ),
        items: lectures,
        showPreviewLink: showLecturePreview,
      });
    }

    if (supplements && supplements.length > 0) {
      materialGroups.push({
        label: (
          <span css={styles.groupLabel}>
            <ReadingCircledIcon size="large" />
            <FormattedMessage
              message={_t('{numberOfItems} {numberOfItems, plural, =1 {reading} other {readings}}')}
              numberOfItems={supplements.length}
            />
          </span>
        ),
        items: supplements,
      });
    }

    if (assessments) {
      AssessmentTypeOrder.forEach((assessmentType) => {
        const assessmentItems = assessments[assessmentType];
        if (assessmentItems?.length > 0) {
          let Icon = AssignmentCircledIcon;
          let label;
          switch (assessmentType) {
            case WeeklyMaterialItemType.AppItem:
              Icon = LTICircledIcon;
              label = (
                <FormattedMessage
                  message={_t('{numberOfItems} {numberOfItems, plural, =1 {app item} other {app items}}')}
                  numberOfItems={assessmentItems.length}
                />
              );
              break;
            case WeeklyMaterialItemType.Assignment:
              Icon = AssignmentCircledIcon;
              label = (
                <FormattedMessage
                  message={_t('{numberOfItems} {numberOfItems, plural, =1 {assignment} other {assignments}}')}
                  numberOfItems={assessmentItems.length}
                />
              );
              break;
            case WeeklyMaterialItemType.DiscussionPrompt:
              Icon = DiscussionPromptCircledIcon;
              label = (
                <FormattedMessage
                  message={_t(
                    '{numberOfItems} {numberOfItems, plural, =1 {discussion prompt} other {discussion prompts}}'
                  )}
                  numberOfItems={assessmentItems.length}
                />
              );
              break;
            case WeeklyMaterialItemType.PeerReview:
              Icon = ReviewCircledIcon;
              label = (
                <FormattedMessage
                  message={_t('{numberOfItems} {numberOfItems, plural, =1 {peer review} other {peer reviews}}')}
                  numberOfItems={assessmentItems.length}
                />
              );
              break;
            case WeeklyMaterialItemType.Plugin:
              Icon = PluginIcon;
              label = (
                <FormattedMessage
                  message={_t('{numberOfItems} {numberOfItems, plural, =1 {plugin} other {plugins}}')}
                  numberOfItems={assessmentItems.length}
                />
              );
              break;
            case WeeklyMaterialItemType.ProgrammingAssignment:
              Icon = ProgrammingAssignmentCircledIcon;
              label = (
                <FormattedMessage
                  message={_t(
                    '{numberOfItems} {numberOfItems, plural, =1 {programming assignment} other {programming assignments}}'
                  )}
                  numberOfItems={assessmentItems.length}
                />
              );
              break;
            case WeeklyMaterialItemType.Quiz:
              Icon = QuizCircledIcon;
              label = (
                <FormattedMessage
                  message={_t('{numberOfItems} {numberOfItems, plural, =1 {quiz} other {quizzes}}')}
                  numberOfItems={assessmentItems.length}
                />
              );
              break;
            case WeeklyMaterialItemType.TeammateReview:
              Icon = ReviewCircledIcon;
              label = (
                <FormattedMessage
                  message={_t('{numberOfItems} {numberOfItems, plural, =1 {teammate review} other {teammate reviews}}')}
                  numberOfItems={assessmentItems.length}
                />
              );
              break;
            case WeeklyMaterialItemType.UngradedLab:
              Icon = WebCircledIcon;
              label = (
                <FormattedMessage
                  message={_t('{numberOfItems} {numberOfItems, plural, =1 {ungraded lab} other {ungraded labs}}')}
                  numberOfItems={assessmentItems.length}
                />
              );
              break;
            case WeeklyMaterialItemType.WiseFlow:
              Icon = AssignmentCircledIcon;
              label = (
                <FormattedMessage
                  message={_t(
                    '{numberOfItems} {numberOfItems, plural, =1 {WISEflow assignment} other {WISEflow assignments}}'
                  )}
                  numberOfItems={assessmentItems.length}
                />
              );
              break;

            default:
              break;
          }
          if (label) {
            materialGroups.push({
              label: (
                <span css={styles.groupLabel}>
                  <Icon size="large" />
                  {label}
                </span>
              ),
              items: assessmentItems,
            });
          }
        }
      });
    }

    return materialGroups;
  }, [lectures, supplements, assessments, showLecturePreview]);

  const materials = useMemo(() => {
    return groups.map((group, index) => {
      return (
        <ModuleMaterialGroup
          items={group.items}
          groupLabel={group.label}
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          showPreviewLink={group.showPreviewLink}
          courseSlug={courseSlug}
          authoringPartnerID={authoringPartnerID}
        />
      );
    });
  }, [groups, courseSlug, authoringPartnerID]);

  return (
    <div>
      {description && (
        <Typography2 component="p" variant="bodyPrimary">
          {description}
        </Typography2>
      )}
      {groups.length > 0 && (
        <Subsection
          title={
            <Typography2 variant="subtitleMedium" component="h4">
              {_t("What's included")}
            </Typography2>
          }
        >
          {isMobile ? (
            <div css={styles.materials}>{materials}</div>
          ) : (
            <React.Fragment>
              <Typography2 component="p" variant="bodyPrimary" css={styles.groupSummary}>
                {groups.map((group, index) => {
                  // eslint-disable-next-line react/no-array-index-key
                  return <React.Fragment key={index}>{group.label}</React.Fragment>;
                })}
              </Typography2>
              <ShowMoreContainer
                aria-label={_t('Info about module content')}
                collapsedLabel={_t('Show info about module content')}
                expandedLabel={_t('Hide info about module content')}
                variant="silent"
                css={styles.showMoreContainer}
              >
                {materials}
              </ShowMoreContainer>
            </React.Fragment>
          )}
        </Subsection>
      )}
    </div>
  );
};

export default ModuleItemContent;
